/**
 * Custom element `<mjl-spotlight>`. Style: `src/styles/` — terpisah dari My Apps.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { attachUiKitShadow, bus, getConfig } from '@mjl/shared';
import {
  fetchMyApps,
  fetchRecommendations,
  fetchSearchCatalog,
  fetchSearchHistory,
  searchSpotlight,
} from './api';
import {
  DEFAULT_SPOTLIGHT_SHORTCUT,
  HISTORY_LIMIT,
  RECOMMENDATION_LIMIT,
  SEARCH_DEBOUNCE_MS,
} from './constants';
import { bindSpotlightShortcut } from './keyboard/shortcut';
import { CatalogSearchIndex } from './search/catalog-search';
import {
  applyNavigate,
  resolveAppNavigate,
  resolveCatalogNavigate,
} from './navigate';
import { widgetCss } from './styles/css';
import type {
  SpotlightHistoryData,
  SpotlightMyAppsData,
  SpotlightRecommendationData,
  SpotlightSearchData,
} from './types';

const SPOTLIGHT_TAG = 'mjl-spotlight';

type NavItemKind = 'history' | 'recommendation' | 'application' | 'search';

interface NavItem {
  id: string;
  kind: NavItemKind;
  primary: string;
  secondary?: string;
  description?: string;
}

@customElement(SPOTLIGHT_TAG)
export class MjlSpotlight extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;

  @state() private query = '';
  @state() private navItems: NavItem[] = [];
  @state() private activeIndex = 0;
  @state() private isSearching = false;
  @state() private loadError: string | null = null;

  @state() private searchData: SpotlightSearchData | null = null;
  @state() private historyData: SpotlightHistoryData | null = null;
  @state() private recommendationData: SpotlightRecommendationData | null = null;
  @state() private myAppsData: SpotlightMyAppsData | null = null;

  private searchDebounce?: ReturnType<typeof setTimeout>;
  private searchAbort?: AbortController;
  private idleAbort?: AbortController;
  private catalogIndex = new CatalogSearchIndex();

  connectedCallback() {
    super.connectedCallback();
    // CSS Spotlight (bukan file CDN bersama) — hanya masuk bundle produk ini.
    void attachUiKitShadow(this, widgetCss);
    const shortcut = getConfig()?.spotlight?.shortcut ?? DEFAULT_SPOTLIGHT_SHORTCUT;

    this.unsubOpen = bus.on('spotlight:open', () => {
      void this.handleOpen();
    });
    this.unsubClose = bus.on('spotlight:close', () => {
      this.handleClose();
    });

    this.shortcutController = bindSpotlightShortcut(() => {
      if (this.open) {
        this.handleClose();
      } else {
        void this.handleOpen();
      }
    }, shortcut);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubOpen?.();
    this.unsubClose?.();
    this.shortcutController?.destroy();
    this.clearSearchDebounce();
    this.searchAbort?.abort();
    this.idleAbort?.abort();
  }

  private unsubOpen?: () => void;
  private unsubClose?: () => void;
  private shortcutController?: { destroy: () => void };

  private async handleOpen() {
    this.open = true;
    this.query = '';
    this.searchData = null;
    this.loadError = null;
    this.activeIndex = 0;
    await this.loadIdleData();
    this.rebuildNavItems();
    await this.updateComplete;
    this.focusInput();
  }

  private handleClose() {
    this.open = false;
    this.clearSearchDebounce();
    this.searchAbort?.abort();
    bus.emit('spotlight:close');
  }

  private clearSearchDebounce() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = undefined;
    }
  }

  private async loadIdleData() {
    this.idleAbort?.abort();
    this.idleAbort = new AbortController();
    const signal = this.idleAbort.signal;

    const [history, recommendations, apps, catalog] = await Promise.all([
      fetchSearchHistory(signal),
      fetchRecommendations(signal),
      fetchMyApps(signal),
      fetchSearchCatalog(signal),
    ]);

    if (signal.aborted) return;

    if (history.data) this.historyData = history.data;
    if (recommendations.data) this.recommendationData = recommendations.data;
    if (apps.data) this.myAppsData = apps.data;
    if (catalog.data) this.catalogIndex.load(catalog.data);

    const firstError = history.error ?? recommendations.error ?? apps.error ?? catalog.error;
    if (firstError) this.loadError = firstError;
  }

  private scheduleSearch(value: string) {
    this.clearSearchDebounce();
    if (!value.trim()) {
      this.searchAbort?.abort();
      this.searchData = null;
      this.isSearching = false;
      this.rebuildNavItems();
      return;
    }

    const localResults = this.catalogIndex.search(value.trim(), 10);
    this.searchData = { query: value.trim(), results: localResults, total: localResults.length };
    this.isSearching = false;
    this.rebuildNavItems();

    this.searchDebounce = setTimeout(() => {
      void this.runSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  }

  private async runSearch(query: string) {
    this.searchAbort?.abort();
    this.searchAbort = new AbortController();
    const signal = this.searchAbort.signal;

    const { error } = await searchSpotlight({ query, per_page: 10 }, signal);
    if (signal.aborted) return;
    if (error) this.loadError = error;
  }

  private rebuildNavItems() {
    const items: NavItem[] = [];

    if (!this.query.trim()) {
      this.historyData?.slice(0, HISTORY_LIMIT).forEach((entry) => {
        items.push({
          id: `history-${entry.id}`,
          kind: 'history',
          primary: entry.query,
        });
      });

      this.recommendationData?.slice(0, RECOMMENDATION_LIMIT).forEach((entry) => {
        const menu = entry.menu_data?.[0];
        items.push({
          id: `reco-${entry.id}`,
          kind: 'recommendation',
          primary: menu?.name ?? entry.query,
          secondary: menu?.application_name,
          description: menu?.description,
        });
      });

      this.myAppsData?.forEach((app) => {
        items.push({
          id: `app-${app.app_id}`,
          kind: 'application',
          primary: app.app_name,
          secondary: app.app_initial_name || app.app_name,
        });
      });
    } else {
      this.searchData?.results?.forEach((result) => {
        items.push({
          id: `search-${result.id}`,
          kind: 'search',
          primary: result.name,
          secondary: result.application_name,
        });
      });
    }

    this.navItems = items;
    this.activeIndex = 0;
  }

  /**
   * Same-app (hostname ∈ my-app) → same tab (SPA).
   * Cross-app → new tab ke origin app_url + path catalog.
   * Current app di-infer dari hostname; tidak perlu init applicationKey.
   */
  private navigateTo(target: ReturnType<typeof resolveCatalogNavigate>) {
    if (!target) return;
    applyNavigate(target);
    this.handleClose();
  }

  private activateItem(item: NavItem) {
    if (item.kind === 'history') {
      this.query = item.primary;
      this.scheduleSearch(item.primary);
      return;
    }

    if (item.kind === 'recommendation') {
      const entry = this.recommendationData?.find((r) => `reco-${r.id}` === item.id);
      const menu = entry?.menu_data?.[0];
      const path = menu?.full_path || menu?.url;
      if (path) {
        this.navigateTo(
          resolveCatalogNavigate({
            apps: this.myAppsData,
            currentHostname: window.location.hostname,
            applicationKey: menu?.application_id,
            path,
          }),
        );
        return;
      }
      if (entry?.query) {
        this.query = entry.query;
        this.scheduleSearch(entry.query);
      }
      return;
    }

    if (item.kind === 'application') {
      const app = this.myAppsData?.find((a) => `app-${a.app_id}` === item.id);
      this.navigateTo(
        resolveAppNavigate({
          app,
          currentHostname: window.location.hostname,
        }),
      );
      return;
    }

    if (item.kind === 'search') {
      const result = this.searchData?.results?.find((r) => `search-${r.id}` === item.id);
      const path = result?.full_path || result?.url;
      this.navigateTo(
        resolveCatalogNavigate({
          apps: this.myAppsData,
          currentHostname: window.location.hostname,
          applicationKey: result?.application_id,
          path,
        }),
      );
    }
  }

  private focusInput() {
    const input = this.renderRoot.querySelector('input[type="search"]');
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.select();
    }
  }

  private scrollActiveIntoView() {
    const active = this.renderRoot.querySelector('[data-nav-active="true"]');
    active?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  private handleQueryInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.query = value;
    this.loadError = null;
    this.scheduleSearch(value);
    if (!value.trim()) {
      this.rebuildNavItems();
    }
  }

  private clearQuery() {
    this.query = '';
    this.searchData = null;
    this.isSearching = false;
    this.clearSearchDebounce();
    this.searchAbort?.abort();
    this.rebuildNavItems();
    this.focusInput();
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.handleClose();
      return;
    }

    if (!this.navItems.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.navItems.length - 1);
      this.scrollActiveIntoView();
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      this.scrollActiveIntoView();
    }
    if (event.key === 'Enter') {
      const item = this.navItems[this.activeIndex];
      if (!item) return;
      event.preventDefault();
      this.activateItem(item);
    }
  }

  private handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.handleClose();
    }
  }

  private formatShortcutLabel(shortcut: string): string {
    return shortcut
      .split('+')
      .map((part) => {
        const lower = part.toLowerCase();
        if (lower === 'mod') return 'Ctrl';
        if (lower === 'shift') return 'Shift';
        if (lower === 'alt') return 'Alt';
        return part.length === 1 ? part.toUpperCase() : part;
      })
      .join('+');
  }

  private renderSectionTitle(title: string) {
    return html`<p class="px-3 pt-3 text-xs font-medium text-grey-500 first:pt-2">${title}</p>`;
  }

  private renderNavRow(item: NavItem, index: number) {
    const isActive = index === this.activeIndex;
    return html`
      <button
        type="button"
        role="option"
        aria-selected=${isActive}
        data-nav-active=${isActive ? 'true' : 'false'}
        class="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm ${isActive
          ? 'bg-grey-50 font-medium text-grey-800'
          : 'text-grey-800 hover:bg-grey-50'}"
        @click=${() => this.activateItem(item)}
      >
        <span>${item.primary}</span>
        ${item.secondary
          ? html`<span class="text-xs text-grey-500">${item.secondary}</span>`
          : nothing}
        ${item.description
          ? html`<span class="text-xs text-grey-500">${item.description}</span>`
          : nothing}
      </button>
    `;
  }

  private renderIdleContent() {
    const historyItems = this.navItems.filter((i) => i.kind === 'history');
    const recoItems = this.navItems.filter((i) => i.kind === 'recommendation');
    const appItems = this.navItems.filter((i) => i.kind === 'application');

    if (!historyItems.length && !recoItems.length && !appItems.length) {
      return html`<p class="px-3 py-4 text-sm text-grey-500">Belum ada data untuk ditampilkan.</p>`;
    }

    let offset = 0;
    const sections = [];

    if (historyItems.length) {
      sections.push(
        this.renderSectionTitle('Pencarian Terakhir'),
        historyItems.map((item) => this.renderNavRow(item, offset++)),
      );
    }
    if (recoItems.length) {
      sections.push(
        this.renderSectionTitle('Rekomendasi'),
        recoItems.map((item) => this.renderNavRow(item, offset++)),
      );
    }
    if (appItems.length) {
      sections.push(
        this.renderSectionTitle('Aplikasi'),
        appItems.map((item) => this.renderNavRow(item, offset++)),
      );
    }

    return sections;
  }

  private renderSearchContent() {
    if (this.isSearching) {
      return html`
        <div class="flex items-center justify-center px-3 py-8">
          <div
            class="size-6 animate-spin rounded-full border-2 border-grey-200 border-t-grey-600"
            role="status"
            aria-label="Memuat"
          ></div>
        </div>
      `;
    }

    if (this.searchData && !this.navItems.length) {
      return html`
        <div class="px-3 py-8 text-center">
          <p class="text-sm font-medium text-grey-800">Tidak ada hasil pencarian</p>
          <p class="mt-1 text-xs text-grey-500">Ubah kata kunci untuk mendapatkan hasil pencarian</p>
        </div>
      `;
    }

    if (this.navItems.length) {
      return html`
        ${this.renderSectionTitle(`Hasil Pencarian (${this.searchData?.results?.length ?? 0})`)}
        ${this.navItems.map((item, index) => this.renderNavRow(item, index))}
      `;
    }

    return nothing;
  }

  render() {
    if (!this.open) return nothing;

    const config = getConfig();
    const placeholder = config?.spotlight?.placeholder ?? 'Cari disini';
    const shortcut = config?.spotlight?.shortcut ?? DEFAULT_SPOTLIGHT_SHORTCUT;
    const hasQuery = Boolean(this.query.trim());

    return html`
      <div
        class="mjl-spotlight-overlay fixed inset-0 flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
        @click=${this.handleBackdropClick}
        @keydown=${this.handleKeyDown}
      >
        <div
          class="flex w-full max-w-lg flex-col overflow-hidden rounded-lg border border-grey-200 bg-white shadow-dialog"
          role="dialog"
          aria-label="Spotlight search"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="flex items-center gap-2 border-b border-grey-200 px-3 py-2">
            <span class="text-sm text-grey-500" aria-hidden="true">⌘</span>
            <input
              type="search"
              .value=${this.query}
              placeholder=${placeholder}
              class="min-w-0 flex-1 bg-transparent text-sm text-grey-800 outline-none placeholder:text-grey-500"
              @input=${this.handleQueryInput}
            />
            ${this.query
              ? html`
                  <button
                    type="button"
                    class="text-xs text-grey-500 hover:text-grey-800"
                    aria-label="Hapus pencarian"
                    @click=${this.clearQuery}
                  >
                    ✕
                  </button>
                `
              : nothing}
          </div>

          <div role="listbox" class="max-h-72 overflow-y-auto py-1">
            ${this.loadError
              ? html`<p class="px-3 py-2 text-xs text-red-600">${this.loadError}</p>`
              : nothing}
            ${hasQuery ? this.renderSearchContent() : this.renderIdleContent()}
          </div>

          <div
            class="flex flex-wrap items-center gap-4 border-t border-grey-200 px-3 py-2 text-xs text-grey-500"
          >
            <span>↑↓ Navigasi</span>
            <span>↵ Pilih</span>
            <span>${this.formatShortcutLabel(shortcut)} Buka pencarian</span>
          </div>
        </div>
      </div>
    `;
  }
}

export function registerSpotlight(): void {
  if (!customElements.get(SPOTLIGHT_TAG)) {
    customElements.define(SPOTLIGHT_TAG, MjlSpotlight);
  }
}

export { SPOTLIGHT_TAG };
